import { motion } from "framer-motion";
import { RevealMask } from "@/components/motion/Reveal";
import ScrambleText from "@/components/motion/ScrambleText";

// One cohesive behind-the-scenes photo wall — candid, unposed stock placeholders
// (swap with real crew photos when available). Spans are tuned to tile into a clean
// rectangle with no ragged edges: 2x4 on mobile, 4x3 on desktop.
const WALL = [
  {
    src: "https://images.unsplash.com/photo-1758691737492-48e8fdd336f7?auto=format&fit=crop&w=1600&q=80",
    alt: "The whole team, mid-celebration",
    span: "col-span-2 md:col-span-2 md:row-span-2",
    testid: "team-fun-image",
    hero: true,
  },
  {
    src: "https://images.unsplash.com/photo-1752650735509-58f11eaa2e10?auto=format&fit=crop&w=900&q=80",
    alt: "A win worth a high-five",
    span: "col-span-1 md:col-span-2",
    testid: "team-fun-gallery-image-0",
  },
  {
    src: "https://images.unsplash.com/photo-1590649917466-06e6e1c3e92d?auto=format&fit=crop&w=900&q=80",
    alt: "Heads-together at the laptop",
    span: "col-span-1 md:col-span-1",
    testid: "team-fun-gallery-image-1",
  },
  {
    src: "https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?auto=format&fit=crop&w=900&q=80",
    alt: "Thinking it out on the whiteboard",
    span: "col-span-1 md:col-span-1",
    testid: "team-fun-gallery-image-2",
  },
  {
    src: "https://images.unsplash.com/photo-1739298061766-e2751d92e9db?auto=format&fit=crop&w=900&q=80",
    alt: "Celebrating a launch together",
    span: "col-span-1 md:col-span-2",
    testid: "team-fun-gallery-image-3",
  },
  {
    src: "https://images.unsplash.com/photo-1758520144661-73849bde0da1?auto=format&fit=crop&w=900&q=80",
    alt: "The lighter side of a long day",
    span: "col-span-2 md:col-span-2",
    testid: "team-fun-gallery-image-4",
  },
];

export default function TeamFun() {
  return (
    <section data-testid="team-fun" className="border-t border-line bg-bone">
      <div className="mx-auto max-w-[1600px] px-5 py-16 sm:py-20 md:py-24 lg:py-32 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="mb-5 max-w-xl sm:mb-6 sm:max-w-2xl md:max-w-3xl">
          <ScrambleText
            as="p"
            text="The team"
            className="mb-4 block text-xs font-bold uppercase tracking-[0.22em] text-accent"
          />
          <RevealMask
            as="h2"
            className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-ink md:text-6xl"
          >
            The team
          </RevealMask>
          <RevealMask
            as="h2"
            delay={0.08}
            className="font-serif text-4xl font-normal italic leading-[0.95] text-ink/70 md:text-6xl"
          >
            behind the work.
          </RevealMask>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
          className="mb-10 w-full text-sm leading-relaxed text-muted sm:mb-12 sm:text-base md:mb-16 md:text-lg lg:text-xl"
        >
          A few of the people, the late nights, and the small wins behind every
          brand we take on. The ones who show up early, stay late, and care about
          the work as if it were their own.
        </motion.p>

        <motion.div
          data-testid="team-fun-gallery"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="grid aspect-[1/2] grid-cols-2 grid-rows-4 gap-1.5 overflow-hidden bg-ink sm:aspect-[16/10] md:aspect-[16/9] md:grid-cols-4 md:grid-rows-3 md:gap-2"
        >
          {WALL.map((img) => (
            <div
              key={img.src}
              className={`group relative overflow-hidden ${img.span}`}
            >
              <img
                data-testid={img.testid}
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-700 ease-out group-hover:scale-[1.05] group-hover:grayscale-0"
              />
              <div className="pointer-events-none absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/10" />
              {img.hero && (
                <span className="pointer-events-none absolute bottom-3 left-3 border border-bone/30 bg-ink/60 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-bone backdrop-blur-sm">
                  Behind the scenes
                </span>
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
